import React from "react";
import { Avatar } from "@mui/material";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";

import ThisTheme from "../../../themes/TMSTheme";

import EventIcon from "@mui/icons-material/Event";
import SchoolIcon from "@mui/icons-material/School";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import HourglassFullIcon from "@mui/icons-material/HourglassFull";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PublicIcon from "@mui/icons-material/Public";

import CheckIcon from "@mui/icons-material/Check";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const StyledBadge = styled(Badge)(({ theme, bgcolor }) => ({
    "& .MuiBadge-badge": {
        height: 24,
        width: 24,
        borderRadius: "50%",
        backgroundColor: bgcolor || "#44b700",
        color: "#44b700",
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        border: `1px solid ${theme.palette.background.paper}`,
        padding: "0 4px",
    },
}));

const TMSAvatar = React.forwardRef(function TMSAvatar(props, ref) {
    const { type, disabled, badge, backgroundColor, ...rest } = props;

    let display = (
        <div {...rest} ref={ref}>
            <IconAvatar
                disabled={disabled}
                type={type}
                backgroundColor={backgroundColor}
            />
        </div>
    );

    switch (badge) {
        case "checked":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <CheckIcon
                            alt="Primary"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                >
                    <IconAvatar
                        disabled={disabled}
                        type={type}
                        backgroundColor={backgroundColor}
                    />
                </StyledBadge>
            );

            break;

        case "checked-blue":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <CheckIcon
                            alt="Primary"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                    bgcolor="#2196F3"
                >
                    <IconAvatar
                        disabled={disabled}
                        type={type}
                        backgroundColor={backgroundColor}
                    />
                </StyledBadge>
            );

            break;

        case "in-progress":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <MoreHorizIcon
                            alt="in-progress"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#2196F3"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;

        case "waiting":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <HourglassFullIcon
                            alt="Primary"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                    bgcolor="#2196F3"
                >
                    <IconAvatar
                        disabled={disabled}
                        type={type}
                        backgroundColor={backgroundColor}
                    />
                </StyledBadge>
            );

            break;

        case "attached":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <AttachFileIcon
                            alt="Associated"
                            sx={{ fontSize: 16, color: "#FFFFFF" }}
                        />
                    }
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );

            break;

        case "warning":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <PriorityHighIcon
                            alt="Warning"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#ff9800"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;

        case "error":
            display = (
                <StyledBadge
                    {...rest}
                    ref={ref}
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    badgeContent={
                        <PriorityHighIcon
                            alt="Error"
                            sx={{
                                fontSize: 16,
                                color: "#FFFFFF",
                            }}
                        />
                    }
                    bgcolor="#BB313D"
                >
                    <IconAvatar disabled={disabled} type={type} />
                </StyledBadge>
            );
            break;

        default:
            break;
    }

    return display;
});

const IconAvatar = ({
    type = null,
    disabled = false,
    backgroundColor = "",
}) => {
    const opacity = disabled ? 0.38 : 1;

    let avatarStyle = {
        backgroundColor: ThisTheme.palette.primary.main,
        color: "#FFF",
    };

    if (backgroundColor?.length > 0) {
        avatarStyle.backgroundColor = backgroundColor;
    }

    let icon = <SchoolIcon />;

    switch (type) {
        case "event":
            icon = <EventIcon />;
            break;

        case "enrolment":
        case "course":
            icon = <SchoolIcon />;
            break;

        case "library":
        case "category":
            icon = <LocalLibraryIcon />;
            break;

        case "skill":
            icon = <PublicIcon />;
            break;

        case "accomplishment":
            icon = <EmojiEventsIcon />;
            break;

        default:
            break;
    }

    return (
        <Avatar
            aria-label="Avatar"
            style={{ ...avatarStyle, opacity: opacity }}
            sx={{ width: 52, height: 52, marginRight: "4px" }}
        >
            {icon}
        </Avatar>
    );
};

export default TMSAvatar;
